import { Condition, Transform } from './types';
import { jqPath, maskStrings, splitJqPath } from './jqPaths';
import { emitsUnquoted, parseJqLiteral } from './transforms';

// Parsing jq expressions back into builder pieces: paths, transform chains and
// the conditions wrapper. parseJq uses these to rebuild the whole builder.

// Matches a jq path expression — covers `.foo.bar`, `.foo[0].bar`, `."weird-key"`.
// Accepts the legacy dot-index form (`.foo.0.bar`) for backward compatibility.
// Matches up to (but not including) whitespace / `|` / `)` delimiters.
//
// NOTE: the character class intentionally over-matches — it accepts `=`, `<`, `>`
// too, so in the comparison regex (`^${PATH_GROUP}\s*(==|!=|>|<)\s*(.+)$`) a
// no-space input like `.foo==5` is first swallowed whole by this greedy group.
// The match only succeeds because the regex engine backtracks the path group far
// enough to let the operator alternation claim `==`. This is harmless for our own
// output (genJq always emits spaces around operators), but keep it in mind before
// narrowing or widening this class.
const PATH_GROUP = String.raw`(\.[^\s|)]+)`;
/** Converts a captured jq path back to the internal dotted form (e.g. `gws[0].time` → `gws.0.time`). */
function jqPathToDotted(jqPathStr: string): string {
  return splitJqPath(jqPathStr).join('.');
}

// ── Transform expression parsing (canonical forms only) ──
//
// Recognizes exactly the jq shapes applyTransforms emits, so newly-created
// transforms round-trip back into pills. Anything else returns null and stays a
// complex field (Code mode) — we never guess at hand-written idioms.

/**
 * True if `s` is a bare jq path the builder can faithfully round-trip: `.a.b`, `.a[0].b`,
 * `."weird-key"`, or the legacy dot-index form `.a.0.b`. Rejects iterate (`.a[]`), dynamic
 * indexing (`.a[.b]`), and anything whose regenerated form would change semantics —
 * misclassifying those as paths silently corrupts the filter on re-save.
 */
export function isStrictPath(s: string): boolean {
  const t = s.trim();
  // Structural pre-check runs on a form with string-literal contents collapsed, so a
  // quoted key may contain spaces or operators (`."cached lat"` — which jqPath emits)
  // without tripping the delimiter/`//` rejections.
  const collapsed = maskStrings(t).replace(/" *"/g, '""');
  if (!/^\.[^\s|()]+$/.test(collapsed) || collapsed.includes('//'))
    return false;
  const segments = splitJqPath(t);
  if (segments.length === 0 || segments.some((seg) => seg === '')) return false;
  // Regenerating must reproduce the original (canonical bracket form or legacy dotted form).
  return jqPath(segments) === t || `.${segments.join('.')}` === t;
}

/** If `s` is fully wrapped in one balanced paren pair, returns the inner content; otherwise null. */
function stripOuterParens(s: string): string | null {
  const t = s.trim();
  if (!t.startsWith('(') || !t.endsWith(')')) return null;
  let depth = 0;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (ch === '"') {
      i++;
      while (i < t.length && t[i] !== '"') {
        if (t[i] === '\\') i++;
        i++;
      }
      continue;
    }
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0 && i !== t.length - 1) return null;
    }
  }
  return t.slice(1, -1);
}

/** Splits `s` on a top-level separator token, respecting nested brackets and string literals. */
function splitTopLevel(s: string, sep: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let last = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      i++;
      while (i < s.length && s[i] !== '"') {
        if (s[i] === '\\') i++;
        i++;
      }
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') depth++;
    else if (ch === ')' || ch === ']' || ch === '}') depth--;
    else if (depth === 0 && s.startsWith(sep, i)) {
      parts.push(s.slice(last, i));
      i += sep.length - 1;
      last = i + 1;
    }
  }
  parts.push(s.slice(last));
  return parts;
}

// Regex fragments for canonical-form recognition.
const JQ_STRING_SRC = String.raw`"(?:[^"\\]|\\.)*"`;
const JQ_NUMBER_SRC = String.raw`-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?`;
export const JQ_LITERAL_SRC = `${JQ_STRING_SRC}|true|false|null|${JQ_NUMBER_SRC}`;

/** Recognizes a single canonical pipe-stage (all transform kinds except coalesce). */
function matchTransformStage(stage: string): Transform | null {
  const s = stage.trim();
  if (s === 'tonumber') return { kind: 'tonumber' };
  if (s === 'tostring') return { kind: 'tostring' };
  if (/^\(\s*tonumber\s*\|\s*todate\s*\)$/.test(s))
    return { kind: 'epoch_iso_s' };
  if (/^\(\s*tonumber\s*\/\s*1000\s*\|\s*floor\s*\|\s*todate\s*\)$/.test(s))
    return { kind: 'epoch_iso_ms' };

  let m: RegExpMatchArray | null;
  if (
    (m = s.match(
      new RegExp(String.raw`^\(\s*tonumber\s*\/\s*(${JQ_NUMBER_SRC})\s*\)$`),
    ))
  ) {
    return { kind: 'divide', arg: m[1] };
  }
  if (
    (m = s.match(
      new RegExp(
        String.raw`^\(\s*tostring\s*\|\s*split\(\s*(${JQ_STRING_SRC})\s*\)\s*\)$`,
      ),
    ))
  ) {
    try {
      return { kind: 'split', arg: JSON.parse(m[1]) };
    } catch {
      return null;
    }
  }
  if (
    (m = s.match(/^\(\s*(\{[\s\S]*\})\s*\[\s*tostring\s*\]\s*\/\/\s*\.\s*\)$/))
  ) {
    try {
      // The emitted object literal is valid JSON (keys and string values via JSON.stringify).
      const obj = JSON.parse(m[1]);
      if (obj === null || typeof obj !== 'object' || Array.isArray(obj))
        return null;
      // An empty-string key is a hand-written idiom (maps blank input) that
      // valueMapObject would silently drop on re-save — keep it in Code mode.
      if (Object.prototype.hasOwnProperty.call(obj, '')) return null;
      // Pills hold each output as text: an object/array output would be flattened to
      // "[object Object]", and a string like "123" would come back as a number.
      const keepsType = (to: unknown) =>
        to === null ||
        (typeof to === 'string' ? !emitsUnquoted(to) : typeof to !== 'object');
      if (!Object.values(obj).every(keepsType)) return null;
      const pairs = Object.entries(obj).map(([from, to]) => ({
        from,
        to: String(to),
      }));
      return { kind: 'value_map', pairs };
    } catch {
      return null;
    }
  }
  // Default accepts ONLY jq literals. A path/expression fallback like `(. // .cached_speed)`
  // must NOT parse as a default transform — regenerating it would rewrite the path into a
  // string constant, silently corrupting the filter.
  if (
    (m = s.match(
      new RegExp(String.raw`^\(\s*\.\s*\/\/\s*(${JQ_LITERAL_SRC})\s*\)$`),
    ))
  ) {
    const arg = parseJqLiteral(m[1]);
    // A quoted "123" / "false" would be re-emitted bare — a different type.
    if (m[1].startsWith('"') && emitsUnquoted(arg)) return null;
    return { kind: 'default', arg };
  }
  return null;
}

/**
 * Parses a canonical transform expression into a base path + transform list.
 * A `null` base (`(null // .f | ...)`, emitted by unmappedFieldExpr) parses to
 * `path: null` — an unmapped field carried entirely by its transforms.
 * Returns null for anything that isn't a shape the builder produces.
 */
export function parseTransformExpression(
  expr: string,
): { path: string[] | null; transforms: Transform[] } | null {
  const inner = stripOuterParens(expr);
  if (inner === null) return null;

  const pipeParts = splitTopLevel(inner, '|');
  let base = pipeParts[0].trim();
  const stageParts = pipeParts.slice(1);

  const transforms: Transform[] = [];

  // Base may itself be a coalesce wrap: `(.x // .y // .z)` or bare `.x // .y`.
  const unwrappedBase = base.startsWith('(') ? stripOuterParens(base) : null;
  if (unwrappedBase !== null) base = unwrappedBase.trim();

  // Every `//`-separated part must be a strict path; the first is the mapping
  // (or the literal `null` for an unmapped field), the rest become chained
  // coalesce transforms in order.
  const coalesceParts = splitTopLevel(base, '//').map((p) => p.trim());
  const mainPart = coalesceParts[0];
  if (mainPart !== 'null' && !isStrictPath(mainPart)) return null;
  if (!coalesceParts.slice(1).every(isStrictPath)) return null;
  for (const fallback of coalesceParts.slice(1)) {
    transforms.push({ kind: 'coalesce', arg: jqPathToDotted(fallback) });
  }

  for (const stage of stageParts) {
    const t = matchTransformStage(stage);
    if (!t) return null;
    transforms.push(t);
  }

  // A bare `(null)` with no transforms is not a builder shape.
  if (mainPart === 'null' && transforms.length === 0) return null;

  return {
    path: mainPart === 'null' ? null : splitJqPath(mainPart),
    transforms,
  };
}

const JQ_NUMBER_ONLY_RE = new RegExp(`^(?:${JQ_NUMBER_SRC})$`);

/**
 * The condition value for a comparison right-hand side, or null when the builder
 * can't re-emit it with the same jq type. Conditions store text: `==`/`!=` re-emit
 * numeric-looking text as a number and anything else as a string, and `>`/`<`
 * always re-emit a number. So `== true`, `== .other`, `== "5"` and `> .limit`
 * stay in Code mode instead of being rewritten.
 */
function comparisonValue(op: string, rhs: string): string | null {
  const s = rhs.trim();
  if (JQ_NUMBER_ONLY_RE.test(s)) return s;
  if (op === '>' || op === '<' || !s.startsWith('"')) return null;
  try {
    const str = JSON.parse(s);
    return typeof str === 'string' && isNaN(Number(str)) ? str : null;
  } catch {
    return null;
  }
}

/** Parses the condition wrapper. Returns recognized conditions plus any clauses it could not parse. */
export function parseConditions(conditionString: string): {
  conds: Condition[];
  unrecognized: string[];
} {
  const conditions: Condition[] = [];
  const unrecognized: string[] = [];

  // Quote/paren-aware split: a condition VALUE containing " and " (e.g. "on and off")
  // must not shatter the clause.
  for (const part of splitTopLevel(conditionString, ' and ')) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const containsMatch = trimmed.match(
      new RegExp(
        `^\\(${PATH_GROUP}\\s*\\|\\s*tostring\\s*\\|\\s*contains\\("([^"]*)"\\)\\)$`,
      ),
    );
    if (containsMatch) {
      conditions.push({
        field: jqPathToDotted(containsMatch[1]),
        op: 'contains',
        value: containsMatch[2],
      });
      continue;
    }

    const notNullMatch = trimmed.match(
      new RegExp(`^${PATH_GROUP}\\s*!=\\s*null$`),
    );
    if (notNullMatch) {
      conditions.push({
        field: jqPathToDotted(notNullMatch[1]),
        op: 'not_null',
        value: '',
      });
      continue;
    }

    const isNullMatch = trimmed.match(
      new RegExp(`^${PATH_GROUP}\\s*==\\s*null$`),
    );
    if (isNullMatch) {
      conditions.push({
        field: jqPathToDotted(isNullMatch[1]),
        op: 'is_null',
        value: '',
      });
      continue;
    }

    const comparisonMatch = trimmed.match(
      new RegExp(`^${PATH_GROUP}\\s*(==|!=|>|<)\\s*(.+)$`),
    );
    if (comparisonMatch) {
      const operatorMap: Record<string, string> = {
        '==': 'equals',
        '!=': 'not_equals',
        '>': 'gt',
        '<': 'lt',
      };
      const value = comparisonValue(comparisonMatch[2], comparisonMatch[3]);
      if (value === null) {
        unrecognized.push(trimmed);
        continue;
      }
      conditions.push({
        field: jqPathToDotted(comparisonMatch[1]),
        op: operatorMap[comparisonMatch[2]],
        value,
      });
      continue;
    }

    // Unrecognized clause — record it so the caller can mark the filter non-round-trippable
    // rather than silently dropping the condition (which would loosen the filter on re-save).
    unrecognized.push(trimmed);
  }

  return { conds: conditions, unrecognized };
}
